import React from "react";
import axios from '../axios';
import ModalContentBaseXl from '../redux/components/modal/modal_content_base_xl';
import ModalAlert from '../redux/components/modal/modal_alert';
import ModalLoader from '../redux/components/modal/modal_loader';
import "babel-polyfill";
import { AiModalBody, AiField, aiInputStyle, AiPrimaryButton, AiResultHeader, AiTextButton } from './ai_modal_parts';

export const ReviseOutlineModal = ({originalTitle, originalBody, eventId}) => {
  const modalRef = React.useRef();
  const errorModalRef = React.useRef();
  const rateLimitModalRef = React.useRef();
  const loaderRef = React.useRef();
  const titleRef = React.useRef(); 
  const bodyRef = React.useRef(); 

  const [hasRevisedOnce, setHasRevisedOnce] = React.useState(false);
  const [eventTitle, setEventTitle] = React.useState('');
  const [eventBody, setEventBody] = React.useState('');
  const [point, setPoint] = React.useState('');
  const [comment, setComment] = React.useState('');
  const [overall, setOverall] = React.useState('');

  React.useEffect(() => {
    setEventTitle(originalTitle);
    setEventBody(originalBody);
  }, [originalTitle, originalBody]);

  const copyToClipboard = async (ref) => {
    try {
      if (ref?.current) {
        await navigator.clipboard.writeText(ref.current.value);
        alert('クリップボードにコピーしました');
      }
    } catch (err) {
      console.error('コピーに失敗しました', err);
      alert('コピーに失敗しました');
    }
  };
  
  const formatText = (text) => {
    return text
      .split('\n')
      .filter((line) => line.trim() !== '')
      .map((line, i) => {
        if (line.startsWith('【') && line.endsWith('】')) {
          return <p key={i} className="mt-2">{line}</p>;
        } else if (/^\d+\./.test(line)) {
          return <p key={i} className="mt-2"><strong>{line}</strong></p>;
        } else {
          return <p key={i}>{line}</p>;
        }
      });
  };

  const close = () => {
    modalRef.current?.closeModal();
  };

  const resultRef = React.useRef(null);

  const handleOpenModal = async () => {
    if (!eventId) {
      console.log('eventId:', eventId);
      modalRef.current?.openModal();
      return;
    }
  
    try {
      const res = await axios.get('/api/event/chatgpt_usage_log_latest', {
        params: { event_id: eventId },
      });
  
      if (res.data?.success) {
        const { point, comment, overall, title, body } = res.data.result;
        setPoint(point);
        setComment(comment);
        setOverall(overall);
        setEventTitle(title);
        setEventBody(body);
        setHasRevisedOnce(true); // 添削済み表示
      } else {
        setHasRevisedOnce(false); // 添削前表示
      }
  
      modalRef.current?.openModal();
    } catch (error) {
      if (error.response?.status === 404) {
        setHasRevisedOnce(false);
      } else {
        console.error('履歴取得に失敗しました:', error);
        setHasRevisedOnce(false);
      }
      modalRef.current?.openModal();
    } finally {
      loaderRef.current?.closeModal();
    }
  };

  const handleReviseClick = async () => {
    loaderRef.current?.openModal(); // ローディング表示
    try {
      const res = await axios.post('/api/event/revise_outline', {
        title: originalTitle,
        body: originalBody,
        event_id: eventId,
      });

      const { 
        point,
        comment,
        overall,
        title: revised_title,
        body: revised_body
      } = res.data?.result || {};

      setPoint(point);
      setComment(comment);
      setOverall(overall);
      setEventTitle(revised_title);
      setEventBody(revised_body);

      setHasRevisedOnce(true);

      setTimeout(() => {
        if (resultRef.current) {
          resultRef.current.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);

    } catch (error) {
      console.error(error);
      if (error.response?.status === 429) {
        rateLimitModalRef.current?.openModal();
      } else {
        errorModalRef.current?.openModal();
      }
    } finally {
      loaderRef.current?.closeModal();
    }
  };

  const previewContent = (
    <button 
      type="button"
      className="a-button a-button--sm a-button--orange"
      style={{borderRadius: 8}}
      onClick={handleOpenModal}
      >文章を添削する</button>);

  const modalContent = (
    <div>
      <div
        className="bg-white w-100 border-bottom"
        style={{
          zIndex: 9998,
          borderRadius: '16px 16px 0 0',
          position: 'fixed',
        }}
      >
        <button
          type="button"
          className="close close__modal position--modalclose"
          aria-label="Close"
          onClick={() => close()}
        >
            <span className="text-fz-24px float-right m-2" aria-hidden="true">
              ×
            </span>
        </button>
      </div>
      <AiModalBody
        title="文章を添削する"
        description="今のイベント名と紹介文をAIが採点し、直し方と修正案を出します。"
      >
        {!hasRevisedOnce ? (
          <AiPrimaryButton onClick={handleReviseClick}>添削する</AiPrimaryButton>
        ) : (
          <>
            <div ref={resultRef} />
            <AiResultHeader title="添削結果" />
            <div style={{ marginBottom: 16 }}>
              <p style={{ fontSize: 12, fontWeight: 800, color: '#666', margin: '0 0 4px' }}>総合得点</p>
              <p style={{ margin: 0 }}>
                <span style={{ fontSize: 28, fontWeight: 800, color: '#333' }}>{point}</span>
                <span style={{ fontSize: 13, color: '#8B9097', marginLeft: 4 }}>/ 100</span>
              </p>
            </div>
            <div style={{ marginBottom: 16 }}>
              <p style={{ fontSize: 13, fontWeight: 800, color: '#333', margin: '0 0 4px' }}>項目ごとの評価</p>
              <div style={{ fontSize: 14, color: '#333', lineHeight: 1.7 }}>{formatText(comment)}</div>
            </div>
            <div style={{ marginBottom: 16 }}>
              <p style={{ fontSize: 13, fontWeight: 800, color: '#333', margin: '0 0 4px' }}>改善のポイント</p>
              <div style={{ fontSize: 14, color: '#333', lineHeight: 1.7 }}>{formatText(overall)}</div>
            </div>

            <AiResultHeader title="修正案" />
            <AiField
              label="イベント名"
              htmlFor="revise-outline-title"
              action={<AiTextButton onClick={() => copyToClipboard(titleRef)}>コピー</AiTextButton>}
            >
              <textarea
                id="revise-outline-title"
                ref={titleRef}
                className="tg-ai-input"
                style={{ ...aiInputStyle, height: 'auto', resize: 'vertical' }}
                rows={2}
                value={eventTitle}
                onChange={(e) => setEventTitle(e.target.value)}
              />
            </AiField>
            <AiField
              label="紹介文"
              htmlFor="revise-outline-body"
              action={<AiTextButton onClick={() => copyToClipboard(bodyRef)}>コピー</AiTextButton>}
            >
              <textarea
                id="revise-outline-body"
                ref={bodyRef}
                className="tg-ai-input"
                style={{ ...aiInputStyle, resize: 'vertical' }}
                rows={12}
                value={eventBody}
                onChange={(e) => setEventBody(e.target.value)}
              />
            </AiField>
            <div style={{ textAlign: 'center', marginTop: 24 }}>
              <AiTextButton onClick={handleReviseClick}>もう一度添削する</AiTextButton>
            </div>
          </>
        )}
      </AiModalBody>
    </div>
  );
  return (
    <React.Fragment>
      <ModalContentBaseXl ref={modalRef} previewContent={previewContent} modalContent={modalContent} />
      <ModalAlert
        ref={errorModalRef}
        id="modal-revised-error"
        title="自動添削失敗"
        content="タイトルと説明の自動添削に失敗しました。時間をおいてもう一度お試しください。"
      />
      <ModalAlert
        ref={rateLimitModalRef}
        id="modal-rate-limit-error"
        title="自動添削失敗"
        content="1日の利用数を超過しました。24時間後にもう一度お試しください。"
      />
      <ModalLoader ref={loaderRef} message="約1分お待ちください" dummyProgressShown />
    </React.Fragment>
  );
};
